import React, { HTMLProps, useEffect } from 'react';
import get from 'lodash/get';
import TextInput from 'components/v2/Input/TextInput';
import { useFormContext } from 'react-hook-form';
import { AdapterProps } from '../types';
import InputLabel from '../../InputLabel/InputLabel';
import FormControl from 'components/v2/FormControl/FormControl';
import { useFormError } from '../utils';
import mergeRefs from '../../../../utils/merge-refs';
import scheduleMicrotask from '../../../../utils/schedule-microtask';

export type TextAdapterProps = AdapterProps['text'] &
  React.ComponentProps<typeof TextInput> &
  HTMLProps<HTMLInputElement>;

const TextAdapter = React.forwardRef((props: TextAdapterProps, ref) => {
  const { label, name, rules, autoFocus, disabled, ...rest } = props;
  const { register, formState } = useFormContext();
  const error = useFormError(get(formState.errors, name));
  const defaultValue = get(formState.defaultValues, name);
  const { ref: formRef, ...inputProps } = register(name, rules);
  const inputRef = React.useRef<HTMLInputElement>();

  useEffect(() => {
    scheduleMicrotask(() => {
      if (autoFocus && inputRef.current) {
        inputRef.current.focus();
      }
    });
  }, [autoFocus]);

  return (
    <FormControl
      size={rest.size}
      defaultValue={defaultValue}
      disabled={disabled}
    >
      {label && <InputLabel>{label}</InputLabel>}
      <TextInput
        ref={mergeRefs(formRef, ref, inputRef)}
        error={error}
        autoFocus={autoFocus}
        {...rest}
        {...inputProps}
      />
    </FormControl>
  );
});

export default TextAdapter;
